import { useState, useCallback, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import SurfaceCard from "@/components/primitives/SurfaceCard";
import { SectionHead } from "@/components/organization/kit";

interface Photo {
  url: string;
  caption?: string;
}

interface PhotoGalleryProps {
  photos: Photo[];
}

const INTERVAL = 5000;
const FADE_MS = 600;

const PhotoGallery = ({ photos }: PhotoGalleryProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<number | null>(null);

  const isSingle = photos.length <= 1;

  const goTo = useCallback((index: number) => {
    if (index === activeIndex || nextIndex !== null) return;
    setNextIndex(index);
    setTimeout(() => {
      setActiveIndex(index);
      setNextIndex(null);
    }, FADE_MS);
  }, [activeIndex, nextIndex]);

  const goNext = useCallback(() => {
    goTo((activeIndex + 1) % photos.length);
  }, [activeIndex, photos.length, goTo]);

  const goPrev = useCallback(() => {
    goTo((activeIndex - 1 + photos.length) % photos.length);
  }, [activeIndex, photos.length, goTo]);

  // Auto-advance
  useEffect(() => {
    if (isSingle || isPaused) return;
    timerRef.current = window.setInterval(() => {
      if (nextIndex === null) goNext();
    }, INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isSingle, isPaused, goNext, nextIndex]);


  if (!photos.length) return null;

  return (
    <SurfaceCard padding="lg" className="rounded-2xl">
      <SectionHead kicker="Life at the team" title="Around the office" />

      {/* Main image area */}
      <div
        className="relative rounded-2xl overflow-hidden group mb-4 aspect-[16/9] bg-muted"
        onMouseEnter={() => !isSingle && setIsPaused(true)}
        onMouseLeave={() => !isSingle && setIsPaused(false)}
      >
        {/* Active image */}
        <img
          src={photos[activeIndex]?.url}
          alt={photos[activeIndex]?.caption || `Photo ${activeIndex + 1}`}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Incoming image (crossfade) */}
        {nextIndex !== null && (
          <img
            src={photos[nextIndex]?.url}
            alt={photos[nextIndex]?.caption || `Photo ${nextIndex + 1}`}
            className="absolute inset-0 w-full h-full object-cover animate-fade-in"
            style={{ animationDuration: `${FADE_MS}ms` }}
          />
        )}

        {/* Caption */}
        {photos[activeIndex]?.caption && nextIndex === null && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground/80 to-transparent p-6">
            <p className="text-primary-foreground text-body">{photos[activeIndex].caption}</p>
          </div>
        )}

        {/* Arrows */}
        {!isSingle && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 hover:bg-white/40"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 hover:bg-white/40"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {photos.length > 1 && (
        <>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {photos.map((photo, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden transition-all duration-150 ${
                  index === activeIndex
                    ? "ring-2 ring-primary ring-offset-2 ring-offset-background"
                    : "opacity-60 hover:opacity-100"
                }`}
              >
                <img
                  src={photo.url}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

        </>
      )}
    </SurfaceCard>
  );
};

export default PhotoGallery;
